import { useState, useEffect, useMemo } from 'react';
import { Link } from 'wouter';
import { customInstance } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { FilterChipGroup } from '@/components/ui/filter-chip.js';
import { MutationButton, type MutationState } from '@/components/ui/mutation-button.js';
import {
  Clock,
  ArrowRight,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Inbox,
  BarChart3,
  Table2,
  Calendar,
  AlertTriangle,
  Info,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { TimesheetView } from './timesheet-view.js';
import { CalendarView } from './calendar-view.js';

interface SummaryItem {
  projectId: string;
  projectName: string;
  pendingCount: number;
  pendingHours: number;
  approvedCount: number;
  approvedHours: number;
  rdHours: number;
  classifyingCount: number;
  failedCount: number;
  haikuFallbackCount: number;
  oldestPendingDate: string | null;
}

// ProjectSummary is now just SummaryItem (backend does the aggregation)
type ProjectSummary = SummaryItem;

type Tab = 'queue' | 'timesheet' | 'calendar';


// ── Capture funnel ────────────────────────────────────────────────────────────

function FunnelArrow() {
  return (
    <svg
      width="18" height="18" viewBox="0 0 18 18" fill="none"
      aria-hidden="true"
      className="text-muted-foreground/30 shrink-0 hidden sm:block"
    >
      <path d="M3 9h12M12 5l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CaptureFunnel({
  captured, classifying, approved, pendingCount, rdHours,
}: {
  captured: number; classifying: number; approved: number; pendingCount: number; rdHours: number;
}) {
  const steps = [
    { label: 'Captured', value: captured, sub: 'this FY', urgent: false, accent: false },
    { label: 'Classifying', value: classifying, sub: 'in progress', urgent: false, accent: true },
    { label: 'Approved', value: approved, sub: rdHours > 0 ? `${rdHours.toFixed(1)} R&D hrs` : '—', urgent: false, accent: false },
    { label: 'Needs review', value: pendingCount, sub: pendingCount > 0 ? 'need attention' : 'all clear', urgent: pendingCount > 0, accent: false },
  ];

  return (
    <div className="rounded-[14px] border border-border bg-card px-5 py-4">
      <div className="flex items-center gap-3 sm:gap-4 overflow-x-auto pb-0.5">
        {steps.map((step, i) => (
          <div key={step.label} className="flex items-center gap-3 sm:gap-4 shrink-0">
            {i > 0 && <FunnelArrow />}
            <div className="flex flex-col gap-0.5">
              <p
                className={`text-2xl font-semibold tracking-tight tabular-nums ${
                  step.urgent
                    ? 'text-amber-600 dark:text-amber-400'
                    : step.accent
                    ? 'text-[oklch(0.50_0.12_240)]'
                    : 'text-foreground'
                }`}
                style={{ fontFamily: 'Geist Mono, monospace' }}
              >
                {step.value}
              </p>
              <p className="text-xs font-medium text-foreground">{step.label}</p>
              <p className="text-[11px] text-muted-foreground leading-tight">{step.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Minimal calendar types for the widget ────────────────────────────────────

interface WidgetCalendarPerson { rawHours: number; }
interface WidgetCalendarDay { date: string; persons: WidgetCalendarPerson[]; }
interface WidgetCalendarData {
  month: string;
  days: WidgetCalendarDay[];
  pendingByDate: Record<string, number>;
}

function WorkCalendarWidget() {
  const [month, setMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState<WidgetCalendarData | null>(null);
  const [loadingCal, setLoadingCal] = useState(false);

  const incomeYear = useMemo(() => {
    const [yStr, mStr] = month.split('-') as [string, string];
    const y = parseInt(yStr, 10);
    const m = parseInt(mStr, 10);
    return m >= 7 ? `FY${y + 1}` : `FY${y}`;
  }, [month]);

  useEffect(() => {
    let live = true;
    setLoadingCal(true);
    void customInstance<{ data: WidgetCalendarData }>({
      url: `/api/dashboard/labour-calendar?incomeYear=${incomeYear}&month=${month}`,
      method: 'GET',
    }).then((res) => {
      if (live) setData(res.data);
    }).catch(() => {
      // widget silently fails — full CalendarView tab is the fallback
    }).finally(() => {
      if (live) setLoadingCal(false);
    });
    return () => { live = false; };
  }, [incomeYear, month]);

  const gridDates = useMemo(() => {
    const [yStr, mStr] = month.split('-') as [string, string];
    const y = parseInt(yStr, 10);
    const m = parseInt(mStr, 10);
    const firstDay = new Date(Date.UTC(y, m - 1, 1));
    const lastDay = new Date(Date.UTC(y, m, 0));

    const dow = firstDay.getUTCDay();
    const startOffset = dow === 0 ? -6 : 1 - dow;
    const gridStart = new Date(firstDay.getTime() + startOffset * 86_400_000);

    const endDow = lastDay.getUTCDay();
    const endOffset = endDow === 0 ? 0 : 7 - endDow;
    const gridEnd = new Date(lastDay.getTime() + endOffset * 86_400_000);

    const dates: string[] = [];
    const cursor = new Date(gridStart);
    while (cursor <= gridEnd) {
      dates.push(cursor.toISOString().slice(0, 10));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return dates;
  }, [month]);

  const dayByDate = useMemo(() => {
    const map = new Map<string, WidgetCalendarDay>();
    if (!data) return map;
    for (const day of data.days) map.set(day.date, day);
    return map;
  }, [data]);

  const maxHours = useMemo(() => {
    if (!data) return 0;
    let max = 0;
    for (const day of data.days) {
      const total = day.persons.reduce((a, p) => a + p.rawHours, 0);
      if (total > max) max = total;
    }
    return max;
  }, [data]);

  const prevMonth = () => {
    const [y, m] = month.split('-').map(Number) as [number, number];
    const d = new Date(Date.UTC(y, m - 2, 1));
    setMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  };

  const nextMonth = () => {
    const [y, m] = month.split('-').map(Number) as [number, number];
    const d = new Date(Date.UTC(y, m, 1));
    setMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  };

  const today = new Date().toISOString().slice(0, 10);
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const isCurrentMonth = month === currentMonthStr;

  const [yStr, mStr] = month.split('-') as [string, string];
  const monthLabel = new Date(Date.UTC(parseInt(yStr, 10), parseInt(mStr, 10) - 1, 1))
    .toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  return (
    <div className="rounded-[14px] border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium">Work calendar</span>
          <span className="text-xs text-muted-foreground">{monthLabel}</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={prevMonth}
            className="rounded p-1 hover:bg-muted transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4 text-muted-foreground" />
          </button>
          {!isCurrentMonth && (
            <button
              onClick={() => setMonth(currentMonthStr)}
              className="text-[11px] text-muted-foreground hover:text-foreground px-1 transition-colors"
            >
              Today
            </button>
          )}
          <button
            onClick={nextMonth}
            className="rounded p-1 hover:bg-muted transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <div key={i} className="text-center text-[10px] font-medium text-muted-foreground/50 py-0.5">
            {d}
          </div>
        ))}
      </div>

      {loadingCal ? (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="h-8 rounded-sm bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {gridDates.map((date) => {
            const inMonth = date.slice(0, 7) === month;
            const day = dayByDate.get(date);
            const totalHours = day ? day.persons.reduce((a, p) => a + p.rawHours, 0) : 0;
            const pending = data?.pendingByDate?.[date] ?? 0;
            const intensity = maxHours > 0 && totalHours > 0 ? totalHours / maxHours : 0;
            const isToday = date === today;

            let bg = 'bg-transparent';
            if (inMonth && totalHours > 0) {
              if (intensity < 0.33) bg = 'bg-[oklch(0.55_0.14_155)]/15';
              else if (intensity < 0.66) bg = 'bg-[oklch(0.55_0.14_155)]/35';
              else bg = 'bg-[oklch(0.55_0.14_155)]/60';
            }

            return (
              <div
                key={date}
                className={`relative h-8 rounded-sm flex flex-col items-center justify-center ${bg} ${!inMonth ? 'opacity-25' : ''} ${isToday ? 'ring-1 ring-inset ring-primary/60' : ''}`}
              >
                <span className={`text-[10px] font-medium leading-none ${inMonth && totalHours > 0 ? 'text-foreground' : 'text-muted-foreground/40'}`}>
                  {parseInt(date.slice(-2), 10)}
                </span>
                {pending > 0 && (
                  <div
                    className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500"
                    aria-label={`${pending} pending`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loadingCal && maxHours > 0 && (
        <div className="flex items-center justify-end gap-4 mt-2">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm bg-amber-500/80" aria-hidden="true" />
            <span className="text-[10px] text-muted-foreground">Pending</span>
          </div>
          <div className="flex items-center gap-0.5">
            <div className="w-2 h-2 rounded-sm bg-[oklch(0.55_0.14_155)]/15" aria-hidden="true" />
            <div className="w-2 h-2 rounded-sm bg-[oklch(0.55_0.14_155)]/35" aria-hidden="true" />
            <div className="w-2 h-2 rounded-sm bg-[oklch(0.55_0.14_155)]/60" aria-hidden="true" />
            <span className="text-[10px] text-muted-foreground ml-1">Low → High</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function TimewritingQueuePage() {
  const [tab, setTab] = useState<Tab>('queue');
  const [summaryItems, setSummaryItems] = useState<SummaryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [resetStuckState, setResetStuckState] = useState<MutationState>('idle');

  async function handleResetStuck() {
    setResetStuckState('saving');
    try {
      await customInstance({
        url: '/api/time-entries/reset-stuck',
        method: 'POST',
      });
      await fetchData();
      setResetStuckState('saved');
      setTimeout(() => setResetStuckState('idle'), 2000);
    } catch {
      setResetStuckState('error');
    }
  }

  async function fetchData() {
    setLoading(true);
    setError(null);
    try {
      const res = await customInstance<{ data: { items: SummaryItem[] } }>({
        url: '/api/time-entries/summary',
        method: 'GET',
      });
      setSummaryItems(res.data.items ?? []);
    } catch {
      setError('Failed to load timewriting data. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchData();
  }, []);

  const summaries = useMemo<ProjectSummary[]>(() => {
    let items = summaryItems;
    if (statusFilter === 'pending') items = summaryItems.filter((s) => s.pendingCount > 0);
    else if (statusFilter === 'classifying') items = summaryItems.filter((s) => s.classifyingCount > 0);

    return items
      .filter((s) => s.pendingCount > 0 || s.classifyingCount > 0 || s.approvedCount > 0)
      .sort((a, b) => b.pendingHours - a.pendingHours || b.rdHours - a.rdHours);
  }, [summaryItems, statusFilter]);

  // ── Unfiltered counts (from summaryItems) — for metric tiles and Needs Unsticking ──
  const capturedAll = summaryItems.reduce(
    (acc, s) => acc + s.pendingCount + s.classifyingCount + s.approvedCount + (s.failedCount ?? 0),
    0,
  );
  const classifyingAll = summaryItems.reduce((acc, s) => acc + (s.classifyingCount ?? 0), 0);
  const approvedAll = summaryItems.reduce((acc, s) => acc + s.approvedCount, 0);
  const pendingAll = summaryItems.reduce((acc, s) => acc + s.pendingCount, 0);
  const failedAll = summaryItems.reduce((acc, s) => acc + (s.failedCount ?? 0), 0);
  const rdHoursAll = summaryItems.reduce((acc, s) => acc + s.rdHours, 0);
  const pendingHoursAll = summaryItems.reduce((acc, s) => acc + s.pendingHours, 0);

  // ── Filtered counts (from summaries) — for overview stats and bar chart ──
  const totalPending = summaries.reduce((acc, s) => acc + s.pendingCount, 0);
  const totalPendingHours = summaries.reduce((acc, s) => acc + s.pendingHours, 0);
  const totalApprovedHours = summaries.reduce((acc, s) => acc + s.approvedHours, 0);
  const totalRdHours = summaries.reduce((acc, s) => acc + s.rdHours, 0);
  const maxRdHours = summaries.reduce((max, s) => Math.max(max, s.rdHours), 0);
  const hasRdData = summaries.some((s) => s.rdHours > 0);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Time</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Every hour captured, classified and accounted for — you only touch the exceptions.
        </p>
      </div>

      {/* ── Capture funnel — always visible ── */}
      {!loading && (
        <CaptureFunnel
          captured={capturedAll}
          classifying={classifyingAll}
          approved={approvedAll}
          pendingCount={pendingAll}
          rdHours={rdHoursAll}
        />
      )}

      {/* ── Inline work calendar heatmap ── */}
      {!loading && <WorkCalendarWidget />}

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 border-b">
        <button
          onClick={() => setTab('queue')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'queue'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Inbox className="h-3.5 w-3.5" aria-hidden="true" />
          Review queue
        </button>
        <button
          onClick={() => setTab('timesheet')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'timesheet'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Table2 className="h-3.5 w-3.5" aria-hidden="true" />
          Timesheet
        </button>
        <button
          onClick={() => setTab('calendar')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'calendar'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
          Calendar
        </button>
      </div>

      {tab === 'timesheet' && <TimesheetView />}
      {tab === 'calendar' && <CalendarView />}

      {tab === 'queue' && (loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
        </div>
      ) : error ? (
        <InlineError message="Failed to load time entries." onRetry={() => void fetchData()} />
      ) : summaryItems.length === 0 ? (
        <div className="text-center py-16">
          <Inbox className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-4 text-muted-foreground font-medium">No time entries to review</p>
          <p className="text-sm text-muted-foreground mt-1">
            Connect a GitHub signal source to start ingesting coding sessions.
          </p>
        </div>
      ) : summaries.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No entries match the selected filter.
        </p>
      ) : (
        <>
          {/* ── Filter chips ── */}
          <FilterChipGroup
            chips={[
              { key: 'all', label: 'All' },
              { key: 'pending', label: 'Pending review', count: pendingAll },
              { key: 'classifying', label: 'Classifying', count: classifyingAll },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />

          {/* ── Overview stats ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border bg-card px-4 py-3">
              <p className="text-xs text-muted-foreground mb-1">Pending review</p>
              <p className="text-2xl font-semibold tabular-nums">
                {totalPendingHours > 0
                  ? `${new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(totalPendingHours)}h`
                  : '—'}
              </p>
              {totalPending > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">{totalPending} {totalPending === 1 ? 'session' : 'sessions'}</p>
              )}
            </div>
            <div className="rounded-lg border bg-card px-4 py-3">
              <p className="text-xs text-muted-foreground mb-1">Total hours approved</p>
              <p className="text-2xl font-semibold tabular-nums">
                {totalApprovedHours > 0
                  ? `${new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(totalApprovedHours)}h`
                  : '—'}
              </p>
            </div>
            <div className="rounded-lg border bg-card px-4 py-3">
              <p className="text-xs text-muted-foreground mb-1">R&D hours approved</p>
              <p className="text-2xl font-semibold tabular-nums">
                {totalRdHours > 0
                  ? `${new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(totalRdHours)}h`
                  : '—'}
              </p>
              {totalApprovedHours > 0 && totalRdHours > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {Math.round((totalRdHours / totalApprovedHours) * 100)}% of approved · raw total
                </p>
              )}
            </div>
          </div>

          {/* ── R&D hours bar chart by project ── */}
          {hasRdData && (
            <div className="rounded-lg border bg-card p-4">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <p className="text-sm font-medium">R&D hours by project</p>
              </div>
              <div className="space-y-3">
                {summaries
                  .filter((s) => s.rdHours > 0)
                  .sort((a, b) => b.rdHours - a.rdHours)
                  .map((s) => {
                    const pct = maxRdHours > 0 ? (s.rdHours / maxRdHours) * 100 : 0;
                    return (
                      <div key={s.projectId} className="flex items-center gap-3">
                        <p
                          className="text-xs text-muted-foreground truncate shrink-0"
                          style={{ width: '10rem' }}
                          title={s.projectName}
                        >
                          {s.projectName}
                        </p>
                        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary/70 transition-all duration-300"
                            style={{ width: `${pct}%` }}
                            aria-label={`${new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(s.rdHours)} R&D hours`}
                          />
                        </div>
                        <p className="text-xs font-medium tabular-nums shrink-0 w-12 text-right">
                          {new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(s.rdHours)}h
                        </p>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* ── Needs Unsticking section ── */}
          {(pendingAll > 0 || classifyingAll > 0 || failedAll > 0) ? (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                Needs Unsticking&nbsp;·&nbsp;{pendingAll + classifyingAll + failedAll}
              </p>

              {/* Low-confidence / pending entries */}
              {pendingAll > 0 && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20 p-4 flex items-start gap-3">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">Low-confidence entries need a human call</p>
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      {pendingAll} {pendingAll === 1 ? 'entry' : 'entries'} ·&nbsp;
                      {pendingHoursAll.toFixed(1)}h pending across {summaryItems.filter((s) => s.pendingCount > 0).length}&nbsp;
                      {summaryItems.filter((s) => s.pendingCount > 0).length === 1 ? 'project' : 'projects'}
                    </p>
                  </div>
                  <button
                    onClick={() => setStatusFilter('pending')}
                    className="text-xs font-medium text-amber-600 dark:text-amber-400 hover:underline shrink-0"
                  >
                    Review →
                  </button>
                </div>
              )}

              {/* Stuck classifying */}
              {classifyingAll > 0 && (
                <div className="rounded-lg border border-sky-500/30 bg-sky-50/50 dark:bg-sky-950/20 p-4 flex items-start gap-3">
                  <Info className="h-4 w-4 text-sky-500 shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {classifyingAll} {classifyingAll === 1 ? 'entry' : 'entries'} stuck in &lsquo;classifying&rsquo; state
                    </p>
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      Classification may have timed out. Re-run to retry.
                    </p>
                  </div>
                  <MutationButton
                    state={resetStuckState}
                    idleLabel="Re-run"
                    savingLabel="Resetting…"
                    savedLabel="Done"
                    variant="secondary"
                    size="sm"
                    onClick={() => void handleResetStuck()}
                  />
                </div>
              )}

              {/* Classification failed */}
              {failedAll > 0 && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
                  <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {failedAll} classification {failedAll === 1 ? 'failure' : 'failures'}
                    </p>
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      These entries could not be classified. Review manually.
                    </p>
                  </div>
                  <button
                    onClick={() => setStatusFilter('all')}
                    className="text-xs font-medium text-destructive hover:underline shrink-0"
                  >
                    Review →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-positive/50 bg-positive-subtle/50 p-4 flex items-center gap-2 text-sm text-positive-foreground">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>All caught up. No pending entries require review.</span>
            </div>
          )}

          {/* ── Project cards ── */}
          <div className="space-y-3">
            {summaries.map((s) => (
              <ProjectCard key={s.projectId} summary={s} />
            ))}
          </div>
        </>
      ))}
    </div>
  );
}

function formatRelativeDate(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return '1 month ago';
  return `${diffMonths} months ago`;
}

function fmtHours(h: number): string {
  return `${new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 }).format(h)}h`;
}

function ProjectCard({ summary }: { summary: ProjectSummary }) {
  const {
    projectId, projectName,
    pendingCount, classifyingCount, approvedCount,
    pendingHours, approvedHours, rdHours,
    oldestPendingDate, failedCount, haikuFallbackCount,
  } = summary;

  return (
    <div className="rounded-lg border bg-card p-4 flex items-center justify-between gap-4">
      <div className="min-w-0 flex-1 space-y-2">
        <h3 className="text-sm font-medium truncate" title={projectName}>{projectName}</h3>

        {/* Time attribution — the headline number */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          {pendingHours > 0 && (
            <span className="text-sm font-semibold tabular-nums text-amber-600 dark:text-amber-400">
              {fmtHours(pendingHours)} pending
            </span>
          )}
          {approvedHours > 0 && (
            <span className="text-sm tabular-nums text-muted-foreground">
              {fmtHours(approvedHours)} approved
              {rdHours > 0 && (
                <span className="text-foreground font-medium"> → {fmtHours(rdHours)} R&D</span>
              )}
            </span>
          )}
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap items-center gap-2">
          {pendingCount > 0 && (
            <Badge variant="warning">
              <Clock className="h-3 w-3" aria-hidden="true" />
              {pendingCount} {pendingCount === 1 ? 'session' : 'sessions'}
            </Badge>
          )}
          {classifyingCount > 0 && (
            <Badge variant="info">
              <Loader2 className="h-3 w-3" aria-hidden="true" />
              {classifyingCount} classifying
            </Badge>
          )}
          {approvedCount > 0 && (
            <Badge variant="positive">
              <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
              {approvedCount} approved
            </Badge>
          )}
          {failedCount > 0 && (
            <Badge variant="destructive">{failedCount} failed</Badge>
          )}
          {haikuFallbackCount > 0 && (
            <Badge variant="warning">{haikuFallbackCount} Haiku</Badge>
          )}
        </div>

        {oldestPendingDate && (
          <p className="text-xs text-muted-foreground">
            Oldest pending: {formatRelativeDate(oldestPendingDate)}
          </p>
        )}
      </div>
      <Link href={`/projects/${projectId}?tab=timewriting`}>
        <Button variant="secondary" size="sm">
          Review
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </Link>
    </div>
  );
}
